import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import NotificationBell from "./NotificationBell";
import { 
  LayoutDashboard, 
  Users as UsersIcon, 
  Phone, 
  CalendarClock, 
  UserCog,
  LogOut,
  X,
  BarChart3,
  ArrowLeftRight,
  Upload,
  Factory,
  Target,
  FileText,
  Package,
  ListChecks,
  Settings2,
  Zap,
  Flame,
  Beaker,
  Database,
} from "lucide-react";
import LocofastLogo from "./LocofastLogo";

const Sidebar = ({ isOpen, onClose, crmType = "domestic" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    localStorage.removeItem('crm_type');
    logout();
    navigate("/login");
  };

  const handleSwitchCRM = () => {
    localStorage.removeItem('crm_type');
    navigate("/select-crm");
  };

  const isExport = crmType === "export";

  // Domestic CRM navigation - base items for all users
  const domesticNavItemsBase = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/leads", icon: UsersIcon, label: "Leads" },
    { path: "/pick-pool", icon: ListChecks, label: "Pick Pool" },
    { path: "/samples", icon: Beaker, label: "Samples" },
    { path: "/orders", icon: Package, label: "Orders" },
    { path: "/calls", icon: Phone, label: "Call History" },
  ];

  // Follow-ups only for non-admin users (agents)
  const domesticNavItems = ['admin','super_admin'].includes(user?.role) 
    ? domesticNavItemsBase 
    : [...domesticNavItemsBase, { path: "/followups", icon: CalendarClock, label: "Follow-ups" }];

  // Export CRM navigation
  const exportNavItems = [
    { path: "/export/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/export/imports", icon: Upload, label: "Imports" },
    { path: "/export/factories", icon: Factory, label: "Factories" },
    { path: "/export/products", icon: Package, label: "Products" },
    { path: "/export/pipeline", icon: Target, label: "Pipeline" },
    { path: "/export/templates", icon: FileText, label: "Templates" },
  ];

  let navItems = isExport ? exportNavItems : domesticNavItems;

  // Admin-only items for Domestic CRM
  if (['admin','super_admin'].includes(user?.role) && !isExport) {
    navItems = [...navItems, 
      { path: "/reports", icon: BarChart3, label: "Reports" },
      { path: "/bd-manager", icon: Flame, label: "BD Manager Review" },
      { path: "/smart-allocation", icon: Zap, label: "Lead Allocation" },
      { path: "/lead-management", icon: Settings2, label: "Lead Deallocation" },
      { path: "/users", icon: UserCog, label: "User Management" }
    ];
  }

  // Super-Admin-only items
  if (user?.role === 'super_admin' && !isExport) {
    navItems = [...navItems, { path: "/backup", icon: Database, label: "Data Backup" }];
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const themeColor = isExport ? "bg-blue-700" : "bg-blue-600";

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center shrink-0">
            <LocofastLogo variant="mark" size={30} tone="white" />
          </div>
          <div className="min-w-0 leading-tight">
            <h1 className="text-white font-bold text-lg tracking-tight">
              Locofast
            </h1>
            <span className="inline-flex mt-1.5 items-center text-[11px] font-semibold uppercase tracking-wider text-blue-100/90 bg-white/10 px-2 py-0.5 rounded">
              {isExport ? "Export CRM" : "Domestic CRM"}
            </span>
          </div>
        </div>
      </div>

      {/* CRM Switcher - Export CRM coming soon */}
      <div
        className="mx-3 mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-white/5 rounded-lg text-white/40 text-sm cursor-not-allowed"
        title="Export CRM coming soon"
        data-testid="switch-crm-btn-disabled"
      >
        <ArrowLeftRight className="w-4 h-4" />
        Switch to Export CRM
        <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded ml-1">Soon</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onClose}
            className={`sidebar-item ${isActive(item.path) ? "active" : ""}`}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-5 h-5" strokeWidth={1.5} />
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>

      {/* User info & Logout */}
      <div className="px-3 py-4 border-t border-white/10">
        <div className="flex items-center gap-2 px-4 mb-3">
          <NotificationBell />
          <span className="text-blue-200 text-xs">Notifications</span>
        </div>
        <div className="px-4 py-3 mb-2">
          <p className="text-white text-sm font-medium truncate">{user?.name}</p>
          <p className="text-blue-200 text-xs truncate">{user?.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-block px-2 py-0.5 bg-white/10 rounded text-xs text-blue-100 capitalize">
              {user?.role}
            </span>
            {user?.crm_type && (
              <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                user.crm_type === 'export' ? 'bg-blue-500/30 text-blue-100' : 'bg-orange-500/30 text-orange-100'
              }`}>
                {user.crm_type}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="sidebar-item w-full text-red-300 hover:text-red-200 hover:bg-red-500/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex fixed left-0 top-0 bottom-0 w-64 ${themeColor} flex-col z-40`}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={onClose}
          />
          <aside className={`absolute left-0 top-0 bottom-0 w-64 ${themeColor} animate-fade-in`}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-white/70 hover:text-white"
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
